import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchCashTransactionsReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import { round2Format } from '../../utils/number';
import type { CashTransactionReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function cashTransactionRowId(row: CashTransactionReportApiItem) {
  return `${row.user_id}-${row.date}-${row.transaction_amount}-${row.category}`;
}

const CASH_TRANSACTIONS_CSV_FILENAME = 'cash-transactions-report.csv';

export function CashTransactionsReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 120 },
    { field: 'date', headerName: 'Date', type: 'dateTime', width: 220 },
    {
      field: 'transaction_amount',
      headerName: 'Transaction amount',
      type: 'number',
      width: 200,
      valueFormatter: (params) => {
        const v = params.value;
        if (v == null || v === '') return '';
        const n = typeof v === 'string' ? parseFloat(v) : Number(v);
        return Number.isFinite(n) ? round2Format(n) : String(v);
      },
    },
    { field: 'category', headerName: 'Category', flex: 1, minWidth: 180 },
  ]);

  const { cashTransactionsItems, totalCashTransactionsItems, fetchCashTransactionsReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    CASH_TRANSACTIONS_CSV_FILENAME,
    useCallback(() => reportService.exportCashTransactionsReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Cash transactions</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchCashTransactionsReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={cashTransactionsItems}
          columns={columns.current}
          getRowId={cashTransactionRowId}
          loading={fetchCashTransactionsReportStatus === fetchStatuses.pending}
          rowCount={totalCashTransactionsItems}
          initialSortModel={[{ field: 'date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
